import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Search } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const DAY_START = 360, DAY_END = 1320, SPAN = DAY_END - DAY_START;
const pct = (m) => `${Math.max(0, Math.min(100, ((m - DAY_START) / SPAN) * 100))}%`;
const wpct = (a, b) => `${Math.max(0, Math.min(100, ((b - a) / SPAN) * 100))}%`;
const ROLE_LABEL = { physician: "Physician", nurse: "Nurse", technician: "Technician", physiotherapist: "Physiotherapist" };

const SKILL_LABELS = {
  vitals: "Vital Signs", phlebotomy: "Phlebotomy", poct: "POCT", rpm_devices: "RPM Setup",
  wound_care: "Wound Care", iv_therapy: "IV Therapy", catheter_care: "Catheter Care",
  medication_admin: "Med Admin", physiotherapy: "Physiotherapy", therapist: "Therapist",
};

function fmtDay(d) {
  const dt = new Date(d + "T00:00:00");
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function shiftDay(d, n) {
  const dt = new Date(d + "T00:00:00"); dt.setDate(dt.getDate() + n);
  return dt.toISOString().slice(0, 10);
}

export default function AvailabilityBoard() {
  const today = new Date().toISOString().slice(0, 10);
  const [day, setDay] = useState(today);
  const [role, setRole] = useState("all");
  const [skill, setSkill] = useState("all");
  const [specialty, setSpecialty] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [skillOptions, setSkillOptions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const cats = await api.get("/lookups/categories");
        const cat = (cats.data.items || cats.data || []).find((c) => c.code === "clinical_skills");
        if (cat) {
          const vals = await api.get("/lookups/values", { params: { category_id: cat.id, status: "active", page_size: 200 } });
          setSkillOptions((vals.data.items || []).map((v) => ({ value: v.value, label: v.label })));
        }
      } catch { /* optional */ }
    })();
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { day };
    if (role !== "all") params.role = role;
    if (skill !== "all") params.skill = skill;
    if (specialty.trim()) params.specialty = specialty.trim();
    api.get("/fleet/availability-board", { params })
      .then((r) => setRows(r.data.rows || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [day, role, skill, specialty]);

  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => { (g[r.role_code] = g[r.role_code] || []).push(r); });
    return g;
  }, [rows]);

  return (
    <div data-testid="availability-board-page">
      <PageHeader title="Provider Availability Board" subtitle="Every field provider's day at a glance — open windows (green), booked blocks (grey) and free slots. Filter by role, skill or specialty." />

      <div className="hah-card p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center gap-1 border border-[#E5E7EB] rounded-lg px-1">
          <button onClick={() => setDay(shiftDay(day, -1))} className="p-1.5 hover:bg-[#F1F5F9] rounded" data-testid="board-prev-day"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-[13px] font-medium text-[#1F2937] px-1 inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[#0B7E8C]" />{day === today ? "Today" : ""} {fmtDay(day)}</span>
          <button onClick={() => setDay(shiftDay(day, 1))} className="p-1.5 hover:bg-[#F1F5F9] rounded" data-testid="board-next-day"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-9 w-40" data-testid="board-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {Object.entries(ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={skill} onValueChange={setSkill}>
          <SelectTrigger className="h-9 w-44" data-testid="board-skill-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All skills</SelectItem>
            {skillOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1 min-w-[180px]">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={specialty} onChange={(e) => setSpecialty(e.target.value)} placeholder="Filter by specialty…" className="bg-transparent outline-none text-sm w-full" data-testid="board-specialty-filter" />
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#6B7280]">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#D1FAE5] inline-block" /> Open</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#94A3B8] inline-block" /> Booked</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#F1F5F9] inline-block border border-[#E5E7EB]" /> Off</span>
        </div>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="hah-card p-10 text-center text-sm text-[#6B7280]" data-testid="board-empty">No providers match these filters.</div>
      ) : (
        Object.entries(grouped).map(([rc, list]) => (
          <div key={rc} className="hah-card p-3 mb-3">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280] mb-2">{ROLE_LABEL[rc] || (rc || "").replace(/_/g, " ")} · {list.length}</div>
            <div className="space-y-2">
              <div className="flex text-[9px] text-[#94A3B8]" style={{ paddingLeft: 180 }}>
                {[6, 10, 14, 18, 22].map((h) => <span key={h} className="flex-1">{`${h}:00`}</span>)}
              </div>
              {list.map((r) => (
                <div key={r.clinician_id} className="flex items-center gap-2" data-testid={`board-row-${r.clinician_id}`}>
                  <div className="w-[172px] shrink-0 min-w-0">
                    <div className="text-[13px] font-medium text-[#1F2937] truncate">{r.name}</div>
                    <div className="text-[10px] text-[#94A3B8] truncate">
                      {r.medical_specialization || (r.skills || []).map((s) => SKILL_LABELS[s] || s).slice(0, 3).join(", ") || "—"}
                    </div>
                  </div>
                  <div className="relative h-7 rounded-md bg-[#F1F5F9] overflow-hidden flex-1" title={r.has_availability ? "" : "No availability set"}>
                    {(r.windows || []).map((w, i) => (
                      <div key={`w${i}`} className="absolute top-0 bottom-0 bg-[#D1FAE5]" style={{ left: pct(w.start_min), width: wpct(w.start_min, w.end_min) }} title={`Open ${w.start}–${w.end}`} />
                    ))}
                    {(r.busy || []).map((b, i) => (
                      <div key={`b${i}`} className="absolute top-0 bottom-0 bg-[#94A3B8] border-x border-white/60" style={{ left: pct(b.start_min), width: wpct(b.start_min, b.end_min) }} title={`${b.label} ${b.start}–${b.end}`} />
                    ))}
                    {!r.has_availability && <div className="absolute inset-0 flex items-center justify-center text-[10px] text-[#94A3B8]">No availability set</div>}
                  </div>
                  <div className="w-16 shrink-0 text-right text-[11px] text-[#6B7280]">{r.open_slots.length} slot{r.open_slots.length === 1 ? "" : "s"}</div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
